import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AlertCircle, Lock, Wrench } from 'lucide-react'
import { getContentList } from '../../lib/api/content'
import { PageHead, Stat, StateChip } from './ui'
import { cn } from '../../lib/cn'

type El = Record<string, unknown>
const isYes = (v: unknown) => v === true || v === 'true' || v === 1 || v === '1'
const num = (v: unknown) => (Number.isFinite(Number(v)) ? Number(v) : 0)
function stripHtml(v: unknown): string {
  return String(v ?? '').replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim()
}
function money(c: unknown): string {
  const n = Number(c)
  return Number.isFinite(n) ? new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(n / 100) : '—'
}

const SEG = [
  { value: 'all', label: 'Tutti' },
  { value: 'active', label: 'Attivi' },
  { value: 'inactive', label: 'Disattivi' },
] as const

type Row = { id: string; name: string; desc: string; price: unknown; maxAvailability: number; enabled: boolean; position: number }

export function ServicesPage() {
  const [seg, setSeg] = useState<(typeof SEG)[number]['value']>('all')

  const list = useQuery({
    queryKey: ['content', 'service', 'all'],
    queryFn: () => getContentList('service', { limit: 200 }),
  })

  const rows = useMemo<Row[]>(() => {
    return (list.data?.data ?? [])
      .map((r) => {
        const el = r.elements as El
        return {
          id: r.id,
          name: String(el.name ?? el.label ?? 'Senza nome'),
          desc: stripHtml(el.description),
          price: el.price,
          maxAvailability: num(el.maxAvailability),
          enabled: isYes(el.enabled),
          position: num(el.position),
        }
      })
      .sort((a, b) => a.position - b.position || a.name.localeCompare(b.name, 'it'))
  }, [list.data])

  const stats = useMemo(() => {
    const active = rows.filter((r) => r.enabled).length
    return { total: rows.length, active }
  }, [rows])

  const filtered = rows.filter((r) =>
    seg === 'active' ? r.enabled : seg === 'inactive' ? !r.enabled : true,
  )

  return (
    <div className="w-full px-6 pb-16 pt-7 lg:px-9">
      <PageHead
        title="Configurazione · Servizi"
        description="Servizi aggiuntivi acquistabili in fase di prenotazione o alla rampa, con prezzo e disponibilità giornaliera."
        model="service"
      />

      <div className="mb-[18px] grid grid-cols-2 gap-[14px] sm:grid-cols-3">
        <Stat label="Servizi" value={String(stats.total)} sub="configurati" />
        <Stat label="Attivi" value={String(stats.active)} sub="acquistabili" />
        <div className="cv-panel hidden items-center px-4 py-3.5 sm:flex">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-oro-soft px-2.5 py-1 text-[12px] font-bold text-oro-700">
            <Lock className="h-3.5 w-3.5" /> Sola lettura
          </span>
        </div>
      </div>

      <div className="mb-[18px] inline-flex overflow-hidden rounded-lg border border-line bg-card">
        {SEG.map((s) => (
          <button
            key={s.value}
            onClick={() => setSeg(s.value)}
            className={cn(
              'px-3.5 py-2 text-[13px] font-semibold transition-colors',
              seg === s.value ? 'bg-lagoon text-white' : 'text-muted hover:text-coal',
            )}
          >
            {s.label}
          </button>
        ))}
      </div>

      {list.isError ? (
        <div className="cv-panel px-4 py-16 text-center text-sm text-muted">
          <AlertCircle className="mx-auto mb-2 h-5 w-5 text-venetian" /> Impossibile caricare i servizi.
        </div>
      ) : (
        <div className="cv-panel overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-[13.5px]">
              <thead>
                <tr className="border-b border-line-soft text-[11px] font-bold uppercase tracking-[1px] text-muted">
                  <th className="px-5 py-3 text-left">Servizio</th>
                  <th className="px-4 py-3 text-right">Prezzo</th>
                  <th className="px-4 py-3 text-right">Disponibilità</th>
                  <th className="px-4 py-3 text-left">Stato</th>
                </tr>
              </thead>
              <tbody>
                {list.isLoading ? (
                  Array.from({ length: 4 }).map((_, i) => (
                    <tr key={i} className="border-b border-line-soft last:border-0">
                      {Array.from({ length: 4 }).map((_c, j) => (
                        <td key={j} className="px-5 py-4">
                          <div className="skeleton h-3.5 w-24 rounded-sm bg-line-soft" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-5 py-16 text-center text-sm text-muted">
                      {rows.length === 0 ? 'Nessun servizio configurato.' : 'Nessun servizio nel filtro.'}
                    </td>
                  </tr>
                ) : (
                  filtered.map((r) => (
                    <tr
                      key={r.id}
                      className={cn('border-b border-line-soft last:border-0 hover:bg-paper', !r.enabled && 'opacity-60')}
                    >
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3">
                          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-paper text-slate ring-1 ring-line">
                            <Wrench className="h-4 w-4" />
                          </span>
                          <div className="min-w-0">
                            <div className="font-semibold text-coal">{r.name}</div>
                            {r.desc && <div className="truncate text-[12px] text-muted">{r.desc}</div>}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right font-mono font-semibold tnum text-coal">{money(r.price)}</td>
                      <td className="px-4 py-3 text-right font-mono tnum text-slate">
                        {r.maxAvailability > 0 ? r.maxAvailability.toLocaleString('it-IT') : '—'}
                      </td>
                      <td className="px-4 py-3">
                        <StateChip on={r.enabled} />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
